import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { getDefaultStore, useAtomValue } from "jotai";
import type { JSX } from "react";

import Clock from "@repo/icons/default/Clock";
import XCircle from "@repo/icons/default/XCircle";
import { CpsAvatar } from "corpus";

import { BatchPatientName } from "@/components/batch-adjust/batch-patient-name";
import {
  FullScreenModal,
  FullScreenModalDescription,
  FullScreenModalHeader,
  FullScreenModalTitle,
} from "@/components/ui/modal/full-screen-modal";
import {
  FullScreenListOption,
  FullScreenListOptionList,
  FullScreenListOptionTitle,
} from "@/components/ui/modal/full-screen-modal-list-option";
import { batchAdjustTargetsAtom } from "@/lib/atoms/batch-adjust-form-atom";
import { toDisplayDate, toDisplayTime } from "@/lib/helpers/batch-adjust";
import { getNameInitials } from "@/lib/string";

const BatchAdjustActionsPage = (): JSX.Element => {
  const router = useRouter();
  const targets = useAtomValue(batchAdjustTargetsAtom);

  const question =
    targets.length === 1
      ? "O que deseja fazer com o agendamento?"
      : `O que deseja fazer com os ${targets.length} agendamentos?`;

  const handleClose = (): void => {
    router.history.back();
  };

  return (
    <FullScreenModal isOpen onClose={handleClose}>
      <div className="flex flex-col gap-4">
        <FullScreenModalHeader>
          <FullScreenModalTitle>{question}</FullScreenModalTitle>
          <FullScreenModalDescription>
            {targets.length > 0 ? toDisplayDate(targets[0].data) : ""}
          </FullScreenModalDescription>
        </FullScreenModalHeader>

        {targets.length > 0 ? (
          <div className="mx-4 rounded-lg border-[1.5px] border-neutral-100 bg-white p-6">
            <div className="flex flex-col divide-y divide-neutral-100">
              {targets.map((target) => (
                <div
                  key={target.codAgendamento}
                  className="flex flex-row items-center gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <CpsAvatar
                    alt={target.nomePaciente}
                    color="primaryLight"
                    initials={getNameInitials(target.nomePaciente) ?? ""}
                    size={40}
                  />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <BatchPatientName
                      nomePaciente={target.nomePaciente}
                      recorrente={target.recorrente}
                    />
                    <span className="text-neutral-500 text-xs">
                      {toDisplayTime(target.horaInicio)} - {toDisplayTime(target.horaFim)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <FullScreenListOptionList>
        <FullScreenListOption
          index={0}
          title="Adiantar horário"
          route="/calendar/batch-adjust/minutes"
          search={{ direction: "advance" }}
          replace={false}
        >
          <Clock size={24} className="fill-primary-500" />
          <FullScreenListOptionTitle>Adiantar horário</FullScreenListOptionTitle>
        </FullScreenListOption>
        <FullScreenListOption
          index={1}
          title="Atrasar horário"
          route="/calendar/batch-adjust/minutes"
          search={{ direction: "delay" }}
          replace={false}
        >
          <Clock size={24} className="fill-primary-500" />
          <FullScreenListOptionTitle>Atrasar horário</FullScreenListOptionTitle>
        </FullScreenListOption>
        <FullScreenListOption
          index={2}
          title="Cancelar agendamentos"
          route="/calendar/batch-adjust/cancel"
          replace={false}
        >
          <XCircle size={24} className="fill-red-300" />
          <h3 className="font-medium text-md text-red-500">Cancelar agendamentos</h3>
        </FullScreenListOption>
      </FullScreenListOptionList>
    </FullScreenModal>
  );
};

export const Route = createFileRoute("/calendar/batch-adjust/")({
  component: BatchAdjustActionsPage,
  beforeLoad: ({ context }) => {
    if (!context.flags["ajuste-em-lote-de-agendamentos"]) {
      redirect({ to: "/calendar", throw: true });
    }
    if (getDefaultStore().get(batchAdjustTargetsAtom).length === 0) {
      redirect({ to: "/calendar", throw: true });
    }
  },
});
